import {
  BoxProps,
  ButtonProps,
  Icon,
  IconProps,
  Input,
  InputElement,
  InputProps,
  Popover,
  PopoverBodyProps,
  PopoverContentProps,
  Portal,
  TextProps,
  useDisclosure,
} from '@chakra-ui/react';
import {
  parseDateAsLocalTime,
  toDateShort,
  toDateTimeShort,
} from '@monetize/utils/core';
import { format as formatDate } from 'date-fns/format';
import { formatISO } from 'date-fns/formatISO';
import { isSameDay } from 'date-fns/isSameDay';
import { isSameMinute } from 'date-fns/isSameMinute';
import { parse as parseDate } from 'date-fns/parse';
import { parseISO } from 'date-fns/parseISO';
import isString from 'lodash/isString';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { IoMdCalendar } from 'react-icons/io';
import { MdArrowDropDown, MdClose } from 'react-icons/md';
import { DATE_PLACEHOLDER } from '../../../constants/placeholders';
import MButton from '../MButton';
import MCircularProgress from '../MCircularProgress';
import MCustomIconButton from '../MCustomIconButton';
import MText from '../MText';
import { FOCUS_WITHOUT_SCROLLING } from '../Overlays';
import { MBox, MFlex } from '../chakra';
import { DatePickerBody } from './DatePickerBody';
import { TimePickerInput } from './TimePickerInput';

function parseDateValue(value: Date | string): Date {
  if (typeof value !== 'string') {
    return value;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parseDateAsLocalTime(value);
  }
  return parseISO(value);
}

function formatDateInput(
  selectedDate?: Date | null,
  includeTime = false,
  customFormatting?: { mode?: 'DATE' | 'MONTH' | 'YEAR' },
) {
  if (!selectedDate) {
    return '';
  }
  if (includeTime) {
    return toDateTimeShort(selectedDate, 'userTimezone');
  }

  // Custom formatting based on mode when customFormatting is enabled
  if (customFormatting?.mode === 'MONTH') {
    return formatDate(selectedDate, 'MMMM yyyy');
  }
  if (customFormatting?.mode === 'YEAR') {
    return formatDate(selectedDate, 'yyyy');
  }

  return toDateShort(selectedDate, 'userTimezone');
}

export interface DatePickerProps extends Omit<
  InputProps,
  'value' | 'onChange'
> {
  value?: Date | string | null;
  popoverContentProps?: PopoverContentProps;
  popoverBodyProps?: PopoverBodyProps;
  readonlyContentProps?: TextProps;
  usePortal?: boolean;
  clearable?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  autoOpen?: boolean;
  includeTime?: boolean;
  minDate?: Date;
  maxDate?: Date;
  triggerType?: 'input' | 'button';
  btnText?: string;
  btnProps?: ButtonProps & { 'data-testid'?: string };
  /**
   * DATE = normal date picker (default) (can also include time picker)
   * MONTH = month picker
   * YEAR = year picker
   */
  mode?: 'DATE' | 'MONTH' | 'YEAR';
  /**
   * Only applies when mode is MONTH
   * Determines what months a user can choose
   * Default is 1, which means all months are available
   * if 3, user can only select every 3 months (quarter)
   * if 6, user can only select every 6 months (semi-annual)
   */
  interval?: 1 | 3 | 6;
  /**
   * Only applies when mode is MONTH
   * Determines what month the interval starts at
   * Only matters is interval is not 1
   */
  anchorMonth?: number;
  /** Only applies for MONTH YEAR mode
   * addMonths from baseDate
   */
  baseDate?: Date;
  /**
   * Enable custom formatting for MONTH and YEAR modes
   * When true, MONTH mode shows "November 2023" and YEAR mode shows "2023"
   * instead of the default formatted date
   */
  useCustomFormatting?: boolean;
  onChange: (date: string | null) => void;
  onClose?: () => void;
  onOpen?: () => void;
  boxProps?: BoxProps;
}

export const DatePicker = forwardRef(
  (
    {
      popoverContentProps,
      popoverBodyProps,
      readonlyContentProps,
      usePortal = false,
      clearable = false,
      isReadOnly = false,
      isDisabled = false,
      isLoading = false,
      includeTime = false,
      autoOpen = false,
      minDate,
      maxDate,
      value,
      btnText = 'Select',
      triggerType = 'input',
      mode = 'DATE',
      interval,
      anchorMonth,
      onChange,
      onClose: onCloseProp,
      onOpen: onOpenProp,
      btnProps = {},
      baseDate,
      useCustomFormatting = false,
      boxProps = {},
      ...inputProps
    }: DatePickerProps,
    ref,
  ) => {
    const isInputTypeTrigger = triggerType === 'input';
    const isButtonTypeTrigger = triggerType === 'button';
    const [offsetDate, onOffsetChange] = useState<Date>(() =>
      value ? parseDateValue(value) : new Date(),
    );

    // Only adjust offsetDate if it's outside bounds and hasn't been set by user interaction
    const boundedOffsetTimestamp = useMemo(() => {
      const offsetTime = offsetDate.getTime();
      const minTime = minDate?.getTime();
      const maxTime = maxDate?.getTime();

      if (minTime && maxTime && minTime > maxTime) {
        return offsetTime;
      }

      if (minTime && offsetTime < minTime) {
        return minTime;
      } else if (maxTime && offsetTime > maxTime) {
        return maxTime;
      }
      return offsetTime;
    }, [offsetDate, minDate, maxDate]);

    // Update offsetDate only when bounds require it
    useEffect(() => {
      if (boundedOffsetTimestamp !== offsetDate.getTime()) {
        onOffsetChange(new Date(boundedOffsetTimestamp));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boundedOffsetTimestamp]);

    const { onOpen, onClose, open } = useDisclosure({
      onClose: onCloseProp,
      onOpen: onOpenProp,
      defaultOpen: autoOpen,
    });

    const [selectedDates, onDatesChange] = useState<Date[]>(() => {
      if (!value) {
        return [];
      }
      if (typeof value === 'string') {
        return [parseDateValue(value)];
      }
      return [value];
    });

    const [selectedDate] = selectedDates;

    const [selectedTime, setSelectedTime] = useState<{
      hour: number;
      minute: number;
      amPm: 'AM' | 'PM';
    }>(() => {
      if (!selectedDate) {
        return {
          hour: 1,
          minute: 0,
          amPm: 'AM',
        };
      }
      const [hour, minute, amPm] = formatDate(selectedDate, 'h mm a').split(
        ' ',
      );
      return {
        hour: Number(hour),
        minute: Number(minute),
        amPm: amPm as 'AM' | 'PM',
      };
    });

    useEffect(() => {
      if (!value) {
        onDatesChange([]);
      } else if (typeof value === 'string') {
        onDatesChange([parseDateValue(value)]);
      } else {
        onDatesChange([value]);
      }
    }, [value]);

    const onPopoverOpen = () => {
      if (!isDisabled) {
        onOpen();
      }
    };

    const onPopoverClose = (date: Date | null = selectedDate) => {
      onClose();
      // emit value only when closed - which is on selection for date, or manually for datetime
      if (!date) {
        // only emit if value changed
        value && onChange(null);
        return;
      }
      if (includeTime) {
        const clonedDate = parseDate(
          `${selectedTime.hour}:${selectedTime.minute} ${selectedTime.amPm}`,
          'h:mm a',
          date || new Date(),
        );
        const tempPriorValue = isString(value) ? parseDateValue(value) : value;
        // only emit if value changed
        if (!tempPriorValue || !isSameMinute(tempPriorValue, clonedDate)) {
          onChange(formatISO(clonedDate, { representation: 'complete' }));
        }
      } else {
        const tempPriorValue = isString(value) ? parseDateValue(value) : value;
        // only emit if value changed
        if (!tempPriorValue || !isSameDay(tempPriorValue, date)) {
          onChange(formatISO(date, { representation: 'date' }));
        }
      }
    };

    const handleKeypress = (event: React.KeyboardEvent) => {
      if (event.code === 'Space' && !open) {
        event.preventDefault();
        onPopoverOpen();
      }
    };

    const handleClear = (event: React.MouseEvent) => {
      onDatesChange([]);
      onPopoverClose(null);
      event.preventDefault();
      onChange(null);
    };

    const handleDateChange = (dates: Date[], shouldClose = true) => {
      const [date] = dates;
      onDatesChange(dates);
      !includeTime && shouldClose && onPopoverClose(date);
    };

    const handleTimeChange = (
      hour: number,
      minute: number,
      amPm: 'AM' | 'PM',
    ) => {
      setSelectedTime({ hour, minute, amPm });
    };

    let inputRightWidth = 8;
    if (clearable && selectedDate) {
      inputRightWidth = 12;
    }

    const PopoverContentWrapper = usePortal ? Portal : React.Fragment;

    if (isReadOnly) {
      return (
        <MText pb="2" {...readonlyContentProps}>
          {formatDateInput(
            selectedDate || value,
            includeTime,
            useCustomFormatting ? { mode } : undefined,
          )}
        </MText>
      );
    }

    const activeProps: InputProps = open
      ? {
          color: 'tIndigo.base',
          borderColor: 'tBlue.lightShade',
          background: 'tBlue.hover',
        }
      : {};
    const activeIconProps: IconProps = open
      ? {
          color: 'tIndigo.base',
        }
      : {};

    const hideFooterShortcuts =
      isDisabled ||
      (!!minDate && new Date() < minDate) ||
      (!!maxDate && new Date() > maxDate);

    return (
      <Popover.Root
        open={open}
        lazyMount
        unmountOnExit
        onOpenChange={(e) => {
          if (e.open) {
            onPopoverOpen();
          } else {
            onPopoverClose();
          }
        }}
        positioning={{
          offset: { mainAxis: 0, crossAxis: 0 },
          strategy: 'fixed',
          placement: 'bottom-end',
        }}
      >
        <Popover.Trigger asChild>
          {/* v3's vertical `Field.Root` no longer stretches its children, so the
              input trigger claims the full width itself. The button trigger stays
              content-sized so the bottom-end popover still anchors to the button. */}
          <MBox w={isInputTypeTrigger ? '100%' : undefined} {...boxProps}>
            {isInputTypeTrigger && (
              <MBox position="relative">
                <InputElement placement="start" w="8" px="0" top="0">
                  <Icon
                    w="5"
                    h="5"
                    tabIndex={-1}
                    color={isDisabled ? 'tGray.acGray' : 'tPurple.dark'}
                    cursor={isDisabled ? 'not-allowed' : 'pointer'}
                    opacity={isDisabled ? 0.25 : 'unset'}
                    {...activeIconProps}
                    asChild
                  >
                    <IoMdCalendar />
                  </Icon>
                </InputElement>
                <Input
                  onKeyUp={handleKeypress}
                  autoComplete="off"
                  readOnly
                  cursor="pointer"
                  userSelect="none"
                  css={{
                    caretColor: 'transparent',
                  }}
                  placeholder={DATE_PLACEHOLDER}
                  value={formatDateInput(
                    selectedDate,
                    includeTime,
                    useCustomFormatting ? { mode } : undefined,
                  )}
                  pl="8"
                  pr={inputRightWidth}
                  disabled={isDisabled}
                  variant="outline"
                  {...activeProps}
                  {...inputProps}
                />
                <InputElement
                  placement="end"
                  w={inputRightWidth}
                  px="0"
                  top="0"
                >
                  {clearable && !isDisabled && selectedDate && (
                    <MCustomIconButton
                      icon={MdClose}
                      aria-label="Clear"
                      onClick={handleClear}
                      bg="none"
                      color="tPurple.dark"
                      _hover={{ bg: 'none' }}
                      _focus={{ color: 'tIndigo.base' }}
                      iconProps={{ ...activeIconProps }}
                    />
                  )}
                  {isLoading ? (
                    <MCircularProgress isIndeterminate size="2xs" />
                  ) : (
                    <Icon
                      tabIndex={-1}
                      size="md"
                      color={isDisabled ? 'tGray.acGray' : 'tPurple.dark'}
                      cursor={isDisabled ? 'not-allowed' : 'pointer'}
                      opacity={isDisabled ? 0.25 : 'unset'}
                      {...activeIconProps}
                      asChild
                    >
                      <MdArrowDropDown />
                    </Icon>
                  )}
                </InputElement>
              </MBox>
            )}
            {isButtonTypeTrigger && (
              <MButton variant="tertiary" size="sm" my={2} {...btnProps}>
                {btnText}
              </MButton>
            )}
          </MBox>
        </Popover.Trigger>
        <PopoverContentWrapper>
          <Popover.Positioner>
            <Popover.Content
              w="100%"
              {...popoverContentProps}
              // When the date-picker is in a nested modal, make sure onBlue events don't bubble up and close the parent modal
              // This only seems to happen in FireFox
              onBlur={(event) => event.preventDefault()}
            >
              <Popover.Body {...popoverBodyProps} minW="304px">
                <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
                  <DatePickerBody
                    isDisabled={isDisabled}
                    minDate={minDate}
                    maxDate={maxDate}
                    selectedDates={selectedDates}
                    mode={mode}
                    interval={interval}
                    anchorMonth={anchorMonth}
                    onDatesChange={handleDateChange}
                    baseDate={baseDate}
                    offsetDate={offsetDate}
                    onOffsetChange={onOffsetChange}
                    onEnter={includeTime ? onPopoverClose : undefined}
                  >
                    {!hideFooterShortcuts &&
                      !includeTime &&
                      mode === 'DATE' && (
                        <MFlex
                          justifyContent="center"
                          mt={2}
                          pt={2}
                          borderTop="1px solid"
                          borderColor="tGray.lightPurple"
                        >
                          <MButton
                            variant="tertiary"
                            size="sm"
                            onClick={() => {
                              const today = new Date();
                              onOffsetChange(today);
                              handleDateChange([today], true);
                            }}
                          >
                            Today
                          </MButton>
                        </MFlex>
                      )}
                  </DatePickerBody>
                  {!hideFooterShortcuts && includeTime && mode === 'DATE' && (
                    <>
                      <TimePickerInput
                        initialValue={selectedDate}
                        isDisabled={isDisabled}
                        onChange={handleTimeChange}
                        onEnter={onPopoverClose}
                      />
                      <MFlex
                        justifyContent="center"
                        mt={2}
                        pt={2}
                        borderTop="1px solid"
                        borderColor="tGray.lightPurple"
                      >
                        <MButton
                          variant="tertiary"
                          size="sm"
                          onClick={() => {
                            const now = new Date();
                            onDatesChange([now]);
                            onOffsetChange(now);
                            if (
                              !selectedDate ||
                              !isSameMinute(selectedDate, now)
                            ) {
                              onChange(
                                formatISO(now, { representation: 'complete' }),
                              );
                            }
                            onClose();
                          }}
                        >
                          Now
                        </MButton>
                      </MFlex>
                    </>
                  )}
                </FocusLock>
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </PopoverContentWrapper>
      </Popover.Root>
    );
  },
);
